import React from "react";
import { formatDurationMinutes } from "@/utils/segmentDuration";

/**
 * Thin horizontal stacked bar showing the proportion of trip time spent in
 * each travel mode. Stays ("at location") is computed as the difference
 * between trip total minutes and total transit minutes, so it visually
 * represents the "non-moving" portion of the trip.
 *
 * Visual goal: minimal, one-line bar with a tiny legend underneath. Zero
 * chrome, no gridlines, no percentages on the bar itself (they live in the
 * tooltips + legend).
 */
const MODES = [
  { key: "stays",     label: "Stays",   color: "bg-cyan-500" },
  { key: "flight",    label: "Flights", color: "bg-sky-500" },
  { key: "train",     label: "Trains",  color: "bg-amber-500" },
  { key: "bus",       label: "Buses",   color: "bg-emerald-500" },
  { key: "cruise",    label: "Cruises", color: "bg-indigo-500" },
  { key: "rideshare", label: "Rides",   color: "bg-teal-500" },
];

const tripMinutesFromTrip = (trip) => {
  if (!trip?.start_date || !trip?.end_date) return 0;
  try {
    const s = new Date(`${trip.start_date}T00:00:00`);
    const e = new Date(`${trip.end_date}T23:59:00`);
    const mins = (e.getTime() - s.getTime()) / 60000;
    return mins > 0 ? mins : 0;
  } catch {
    return 0;
  }
};

const TransitDensityBar = ({ trip, travelSummary }) => {
  if (!travelSummary) return null;
  const tripMinutes = tripMinutesFromTrip(trip);
  const transitMinutes = travelSummary.totalTransitMinutes || 0;
  if (tripMinutes <= 0 && transitMinutes <= 0) return null;

  const stayMinutes = Math.max(0, tripMinutes - transitMinutes);
  const total = tripMinutes > 0 ? tripMinutes : transitMinutes;

  const slices = MODES
    .map(m => {
      const mins = m.key === "stays" ? stayMinutes : (travelSummary[m.key]?.totalMinutes || 0);
      return { ...m, minutes: mins, pct: total > 0 ? (mins / total) * 100 : 0 };
    })
    .filter(s => s.minutes > 0);

  if (slices.length === 0) return null;

  return (
    <div data-testid="transit-density-bar" className="mt-5">
      <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
        How the trip broke down
      </div>
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
        {slices.map(s => (
          <div
            key={s.key}
            className={`h-full ${s.color} transition-all`}
            style={{ width: `${s.pct}%` }}
            title={`${s.label}: ${formatDurationMinutes(s.minutes) || "0m"} (${s.pct.toFixed(1)}%)`}
            data-testid={`transit-density-slice-${s.key}`}
          />
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-600 dark:text-gray-400">
        {slices.map(s => (
          <span key={s.key} className="inline-flex items-center gap-1.5">
            <span className={`inline-block w-2 h-2 rounded-sm ${s.color}`} />
            <span>{s.label}</span>
            <span className="text-gray-400 dark:text-gray-500">{s.pct.toFixed(0)}%</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export default TransitDensityBar;
